import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Star, BellSimple, BellSlash, Trash, SignOut, Envelope, BellRinging, Moon, ClockCounterClockwise } from "@phosphor-icons/react";
import { AppHeader } from "../components/AppHeader";
import { Disclaimer } from "../components/Disclaimer";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useFavorites } from "../hooks/useFavorites";
import { usePush } from "../hooks/usePush";
import { InstallCard } from "../components/InstallButton";
import api, { formatApiError } from "../lib/api";
import { fmtDate, timeAgo } from "../lib/format";

function Notifications() {
  const { supported, enabled, busy, enable, disable } = usePush();
  return (
    <div className="border border-border bg-card p-5">
      <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-2"><BellRinging size={14} /> Browser Notifications</h3>
      {!supported ? (
        <p className="text-sm text-muted-foreground" data-testid="push-unsupported">
          Your browser does not support web push notifications. In-app alerts will still appear while the app is open.
        </p>
      ) : (
        <div className="flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            {enabled ? "Push notifications are enabled — you'll be alerted even when this tab is in the background."
                     : "Enable free browser push to receive price alerts even when the app is not open."}
          </p>
          <Button onClick={enabled ? disable : enable} disabled={busy} variant={enabled ? "outline" : "default"}
            className="rounded-none shrink-0" data-testid="push-toggle">
            {busy ? "…" : enabled ? "Disable" : "Enable"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [np, setNp] = useState("");
  const mut = useMutation({
    mutationFn: async () => (await api.post("/auth/change-password", { current_password: cur, new_password: np })).data,
    onSuccess: () => { toast.success("Password updated"); setCur(""); setNp(""); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  return (
    <div className="border border-border bg-card p-5">
      <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4">Change Password</h3>
      <div className="space-y-3 max-w-sm">
        <div className="space-y-2">
          <Label>Current Password</Label>
          <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" className="rounded-none" />
        </div>
        <div className="space-y-2">
          <Label>New Password</Label>
          <Input type="password" value={np} onChange={(e) => setNp(e.target.value)} data-testid="cp-new" className="rounded-none" placeholder="Min 8 chars" />
        </div>
        <Button onClick={() => mut.mutate()} disabled={!cur || np.length < 8 || mut.isPending} className="rounded-none" data-testid="cp-submit">
          {mut.isPending ? "Updating…" : "Update Password"}
        </Button>
      </div>
    </div>
  );
}

function Favorites() {
  const { favs, toggle } = useFavorites();
  const nav = useNavigate();
  return (
    <div className="border border-border bg-card p-5">
      <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-2"><Star size={14} /> Favorites</h3>
      {favs.length === 0 ? <p className="text-sm text-muted-foreground">No favorites yet.</p> : (
        <div className="divide-y divide-border" data-testid="account-favorites">
          {favs.map((f) => (
            <div key={f.id} className="flex items-center justify-between py-2.5">
              <button onClick={() => nav(f.asset_type === "crypto" ? `/crypto/${f.coin_id}` : `/currency/${f.symbol}`)} className="flex items-center gap-2 text-left">
                <span className="font-heading font-bold">{f.symbol}</span>
                <span className="text-xs text-muted-foreground">{f.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5">{f.asset_type}</span>
              </button>
              <button onClick={() => toggle({ asset_type: f.asset_type, symbol: f.symbol, coin_id: f.coin_id })} data-testid={`remove-fav-${f.symbol.toLowerCase()}`} className="text-muted-foreground hover:text-danger">
                <Trash size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Alerts() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["alerts"], queryFn: async () => (await api.get("/alerts")).data.alerts });
  const toggle = useMutation({
    mutationFn: async ({ id, enabled }) => (await api.patch(`/alerts/${id}`, { enabled })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["alerts"] }),
  });
  const cooldown = useMutation({
    mutationFn: async ({ id, cooldown_hours }) => (await api.patch(`/alerts/${id}`, { cooldown_hours })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("Cooldown updated"); },
  });
  const snooze = useMutation({
    mutationFn: async ({ id, preset }) => (await api.post(`/alerts/${id}/snooze`, { preset })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("Alert snoozed"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const resume = useMutation({
    mutationFn: async (id) => (await api.post(`/alerts/${id}/resume`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("Alert resumed"); },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/alerts/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alerts"] }); toast.success("Alert deleted"); },
  });
  const alerts = data || [];
  const label = (a) => a.alert_type === "above" ? "Rises above" : a.alert_type === "below" ? "Falls below" : "% change ≥";
  const isSnoozed = (a) => a.snoozed_until && new Date(a.snoozed_until) > new Date();
  const resumeLabel = (iso) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  return (
    <div className="border border-border bg-card p-5">
      <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground mb-4 flex items-center gap-2"><BellSimple size={14} /> Alerts</h3>
      {alerts.length === 0 ? <p className="text-sm text-muted-foreground">No alerts set. Create one from any asset's detail page.</p> : (
        <div className="divide-y divide-border" data-testid="account-alerts">
          {alerts.map((a) => {
            const snoozed = isSnoozed(a);
            return (
            <div key={a.id} className="flex items-center justify-between py-3 gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold">{a.symbol}</span>
                  {snoozed && <span className="text-[10px] uppercase text-primary border border-primary/40 px-1.5 flex items-center gap-1" data-testid={`snoozed-badge-${a.id}`}><Moon size={10} weight="fill" /> Snoozed · resumes {resumeLabel(a.snoozed_until)}</span>}
                  {!snoozed && a.triggered && <span className="text-[10px] uppercase text-success border border-success/40 px-1.5">triggered</span>}
                </div>
                <div className="text-xs text-muted-foreground font-mono">{label(a)} {a.target_value}{a.alert_type === "pct_change" ? "%" : ` ${a.base || "USD"}`}</div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                {snoozed ? (
                  <button onClick={() => resume.mutate(a.id)} data-testid={`alert-resume-${a.id}`} title="Resume now"
                    className="text-xs border border-border px-2 py-1 hover-lift">Resume</button>
                ) : (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button data-testid={`alert-snooze-${a.id}`} title="Snooze alert" className="text-muted-foreground hover:text-primary p-1">
                        <BellSlash size={16} />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-muted-foreground">Snooze for</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {[["1h", "1 hour"], ["4h", "4 hours"], ["12h", "12 hours"], ["tomorrow", "Until tomorrow"]].map(([p, l]) => (
                        <DropdownMenuItem key={p} onClick={() => snooze.mutate({ id: a.id, preset: p })} data-testid={`snooze-${p}-${a.id}`}>{l}</DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                <select value={a.cooldown_hours || 24} onChange={(e) => cooldown.mutate({ id: a.id, cooldown_hours: Number(e.target.value) })}
                  data-testid={`alert-cooldown-select-${a.id}`} title="Notification cooldown"
                  className="h-8 border border-input bg-background px-2 text-xs font-mono">
                  {[1, 4, 12, 24].map((h) => <option key={h} value={h}>{h}h</option>)}
                </select>
                <Switch checked={a.enabled} onCheckedChange={(v) => toggle.mutate({ id: a.id, enabled: v })} data-testid={`alert-toggle-${a.id}`} />
                <button onClick={() => del.mutate(a.id)} data-testid={`alert-delete-${a.id}`} className="text-muted-foreground hover:text-danger">
                  <Trash size={16} />
                </button>
              </div>
            </div>
          );})}
        </div>
      )}
    </div>
  );
}

function AlertHistory() {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["alert-history"], queryFn: async () => (await api.get("/alerts/history")).data.history });
  const clear = useMutation({
    mutationFn: async () => (await api.delete("/alerts/history")).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["alert-history"] }); toast.success("History cleared"); },
    onError: (e) => toast.error(formatApiError(e)),
  });
  const hist = data || [];
  return (
    <div className="border border-border bg-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xs font-bold tracking-[0.2em] uppercase text-muted-foreground flex items-center gap-2"><ClockCounterClockwise size={14} /> Alert History</h3>
        {hist.length > 0 && (
          <button onClick={() => clear.mutate()} data-testid="clear-alert-history" className="text-xs text-muted-foreground hover:text-danger inline-flex items-center gap-1">
            <Trash size={13} /> Clear
          </button>
        )}
      </div>
      {hist.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="alert-history-empty">No alerts have triggered yet. Triggered alerts will appear here.</p>
      ) : (
        <div className="divide-y divide-border" data-testid="alert-history-list">
          {hist.map((h) => (
            <div key={h.id} className="flex items-start justify-between gap-3 py-2.5" data-testid={`alert-history-${h.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-bold">{h.symbol}</span>
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground border border-border px-1.5">{h.asset_type}</span>
                </div>
                <div className="text-sm text-muted-foreground mt-0.5 break-words">{h.message}</div>
              </div>
              <span className="text-xs text-muted-foreground font-mono whitespace-nowrap shrink-0" title={fmtDate(h.at)}>{timeAgo(h.at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


export default function Account() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-8">
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mb-8">Account</h1>

        <div className="border border-border bg-card p-5 mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center bg-primary/10 text-primary"><Envelope size={20} /></div>
            <div>
              <div className="font-mono font-semibold" data-testid="account-email">{user?.email}</div>
              <div className="text-xs text-muted-foreground">Member since {fmtDate(user?.created_at)}</div>
            </div>
          </div>
          <Button variant="outline" onClick={doLogout} className="rounded-none text-danger" data-testid="account-logout">
            <SignOut size={16} className="mr-2" /> Log Out
          </Button>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <ChangePassword />
          <Favorites />
          <div className="md:col-span-2"><InstallCard /></div>
          <div className="md:col-span-2"><Notifications /></div>
          <div className="md:col-span-2"><Alerts /></div>
          <div className="md:col-span-2"><AlertHistory /></div>
        </div>
        <Disclaimer />
      </main>
    </div>
  );
}
